import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar, FileText, Search } from 'lucide-react';
import { format, parseISO, startOfDay, endOfDay } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI, timesheetAPI, userAPI } from '../services/api';

export default function AdminDraftProjectDay() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const projectName = searchParams.get('project') || '';
  const selectedDate = searchParams.get('date') || format(new Date(), 'yyyy-MM-dd');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: assignedProjects = [], isLoading: projectsLoading } = useQuery(
    'admin-assigned-projects',
    () => projectAPI.getAssignedProjects(),
    {
      enabled: !!user && user?.role !== 'SUPER_ADMIN',
      select: (response) => response?.data?.projects || response?.data || [],
    }
  );

  const assignedProjectIds = useMemo(() => {
    if (user?.role === 'SUPER_ADMIN') return null;
    return new Set((assignedProjects || []).map((p) => p.id));
  }, [assignedProjects, user?.role]);

  const { data: allUsers = [], isLoading: usersLoading } = useQuery(
    'all-users-for-draft-project-day',
    () => {
      if (user?.role === 'SUPER_ADMIN') {
        return userAPI.getAllEmployees().then((res) => {
          const employees = res?.data?.data || res?.data || [];
          return userAPI.getAllManagers().then((managersRes) => {
            const managers = managersRes?.data?.data || managersRes?.data || [];
            return [...employees, ...managers];
          });
        });
      }
      return userAPI.getManagersAndEmployeesAdmin().then((res) => res?.data?.data || res?.data || []);
    },
    { enabled: !!user, select: (data) => data || [] }
  );

  const { data: timesheetRows = [], isLoading: draftsLoading } = useQuery(
    ['draft-project-day', projectName, selectedDate, allUsers, Array.from(assignedProjectIds || [])],
    async () => {
      if (!projectName) return [];
      if (!allUsers || allUsers.length === 0) return [];

      const selectedDateObj = parseISO(selectedDate);
      const dayStart = startOfDay(selectedDateObj);
      const dayEnd = endOfDay(selectedDateObj);

      // If admin has no assigned projects, show nothing
      if (assignedProjectIds !== null && assignedProjectIds.size === 0) return [];

      const perUser = await Promise.all(
        allUsers.map(async (u) => {
          try {
            const resp =
              user?.role === 'SUPER_ADMIN'
                ? await timesheetAPI.getSuperAdminUserTimesheets(u.id)
                : await timesheetAPI.getUserTimesheets(u.id);
            const timesheets = resp?.data?.timesheets || resp?.data || [];

            const roleValue = typeof u.role === 'object' && u.role !== null ? u.role.name : u.role;
            const employeeName = `${u.firstName || ''} ${u.lastName || ''}`.trim();
            const baseManagerName =
              u.manager?.firstName && u.manager?.lastName ? `${u.manager.firstName} ${u.manager.lastName}` : '';

            return timesheets.flatMap((ts) => {
              if (ts.status !== 'DRAFT') return [];

              const startDate = parseISO(ts.startDate);
              const endDate = parseISO(ts.endDate);
              if (!(selectedDateObj >= startDate && selectedDateObj <= endDate)) return [];

              const entries = ts.entries || [];
              const matchedEntries = entries
                .map((e) => {
                  const name = e.project?.name || e.projectName || (typeof e.project === 'string' ? e.project : null);
                  const pid = e.project?.id || e.projectId || null;
                  const entryDateRaw = e.date || e.entryDate;
                  const entryDate = entryDateRaw ? parseISO(String(entryDateRaw)) : null;
                  return {
                    projectName: name,
                    projectId: pid,
                    entryDate,
                    hours: e.hours || e.hoursWorked || 0,
                    description: e.task || e.taskDescription || e.description || 'No description',
                  };
                })
                .filter((e) => {
                  if (!e.projectName || e.projectName !== projectName) return false;
                  if (!e.entryDate) return false;
                  if (!(e.entryDate >= dayStart && e.entryDate <= dayEnd)) return false;

                  if (assignedProjectIds === null) return true; // super admin
                  if (!e.projectId) return false;
                  return assignedProjectIds.has(e.projectId);
                });

              if (matchedEntries.length === 0) return [];

              const managerName = ts.assignedByManagerNames || baseManagerName || 'No Manager';

              const draftSavedAtRaw = ts.updatedAt || ts.createdAt || null;
              const draftSavedAt = draftSavedAtRaw ? String(draftSavedAtRaw) : null;

              return [
                {
                  id: ts.id,
                  employeeName,
                  employeeEmail: u.email,
                  employeeRole: roleValue,
                  managerName,
                  weekRange: `${format(parseISO(ts.startDate), 'MMM dd')} - ${format(parseISO(ts.endDate), 'MMM dd, yyyy')}`,
                  totalHours: ts.totalHours || 0,
                  draftSavedAt,
                  entries: matchedEntries.map((me) => ({
                    date: format(me.entryDate, 'MMM dd, yyyy'),
                    hours: me.hours,
                    description: me.description,
                  })),
                },
              ];
            });
          } catch {
            return [];
          }
        })
      );

      return perUser.flat();
    },
    {
      enabled:
        !!user &&
        !!projectName &&
        (user?.role === 'SUPER_ADMIN' || assignedProjects !== undefined) &&
        allUsers.length > 0,
    }
  );

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return timesheetRows;
    const s = searchTerm.toLowerCase();
    return timesheetRows.filter((r) => {
      return (
        r.employeeName?.toLowerCase().includes(s) ||
        r.employeeEmail?.toLowerCase().includes(s) ||
        String(r.id).includes(s) ||
        r.managerName?.toLowerCase().includes(s) ||
        r.entries?.some((e) => String(e.description || '').toLowerCase().includes(s))
      );
    });
  }, [timesheetRows, searchTerm]);

  const groupedByEmployee = useMemo(() => {
    const map = new Map();
    for (const r of filteredRows) {
      const key = r.employeeEmail || r.employeeName || String(r.id);
      if (!map.has(key)) {
        map.set(key, {
          employeeName: r.employeeName,
          employeeEmail: r.employeeEmail,
          employeeRole: r.employeeRole,
          timesheets: [],
        });
      }
      map.get(key).timesheets.push(r);
    }
    return Array.from(map.values()).sort((a, b) => {
      const an = (a.employeeName || '').toLowerCase();
      const bn = (b.employeeName || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return (a.employeeEmail || '').toLowerCase().localeCompare((b.employeeEmail || '').toLowerCase());
    });
  }, [filteredRows]);

  const isLoading = usersLoading || draftsLoading || projectsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between">
        <div>
          <button
            onClick={() => navigate('/admin/draft-report')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Draft Report
          </button>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">
            {projectName || 'Project'} — Drafts for {format(parseISO(selectedDate), 'MMM dd, yyyy')}
          </h1>
          <p className="text-gray-600 mt-1">Entry details (paragraph view) grouped by employee.</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>{format(parseISO(selectedDate), 'MMM dd, yyyy')}</span>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by employee, email, manager, timesheet ID, or entry description..."
                className="form-input pl-10 text-gray-900 placeholder:text-gray-400"
              />
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg shadow-sm overflow-hidden">
        {groupedByEmployee.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No drafts found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {user?.role !== 'SUPER_ADMIN' && assignedProjectIds !== null && assignedProjectIds.size === 0
                ? 'You are not assigned to any projects. Please contact Super Admin.'
                : 'No draft entry data matches this project/date.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {groupedByEmployee.map((g) => (
              <div key={g.employeeEmail || g.employeeName} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-base font-semibold text-gray-900">{g.employeeName || 'Unknown'}</div>
                    <div className="text-sm text-gray-600">{g.employeeEmail || ''}</div>
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 font-medium">{g.employeeRole || 'N/A'}</span>
                    <span className="ml-2">{g.timesheets.length} draft timesheet(s)</span>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  {g.timesheets.map((ts) => (
                    <div key={ts.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="text-sm text-gray-800">
                          <span className="font-semibold">Timesheet ID:</span> {ts.id}
                          <span className="mx-2 text-gray-400">|</span>
                          <span className="font-semibold">Week:</span> {ts.weekRange}
                          {ts.draftSavedAt && (
                            <>
                              <span className="mx-2 text-gray-400">|</span>
                              <span className="font-semibold">Draft Time:</span>{' '}
                              {(() => {
                                try {
                                  return format(parseISO(ts.draftSavedAt), 'MMM dd, yyyy hh:mm a');
                                } catch {
                                  return ts.draftSavedAt;
                                }
                              })()}
                            </>
                          )}
                        </div>
                        <div className="text-sm text-gray-800">
                          <span className="font-semibold">Manager:</span> {ts.managerName || 'No Manager'}
                          <span className="mx-2 text-gray-400">|</span>
                          <span className="font-semibold">Total Hours:</span> {ts.totalHours}
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="text-sm font-semibold text-gray-900 mb-2">Entries</div>
                        <div className="space-y-2">
                          {ts.entries.map((e, idx) => (
                            <p key={idx} className="text-sm text-gray-800">
                              <span className="font-medium">{e.date}</span> — <span className="font-medium">{e.hours}h</span> — {e.description}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}

